import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  Loader2, Calculator, Lock, Send, Users2, PlayCircle, Timer,
  Zap, Wallet, AlertCircle, CheckCircle2,
} from "lucide-react";

const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;
const monthLabel = (m) => {
  if (!m) return "";
  const [y, mm] = m.split("-").map(Number);
  return new Date(y, mm - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
};

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

/**
 * Admin Earnings & Payouts console — the source of truth for the monthly
 * commissions computation and Paystack transfer disbursement.
 */
export default function AdminEarnings() {
  const [periods, setPeriods] = useState([]);
  const [selected, setSelected] = useState(null); // month key
  const [period, setPeriod] = useState(null);
  const [creatorRows, setCreatorRows] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);

  const loadPeriods = async () => {
    const r = await api.get("/admin/earnings/periods").catch(() => ({ data: [] }));
    setPeriods(r.data || []);
    if (!selected) {
      const first = (r.data || [])[0]?.month || currentMonthKey();
      setSelected(first);
    }
  };

  const loadPeriodDetail = async (month) => {
    if (!month) return;
    setBusy(true);
    try {
      const [p, cr, po] = await Promise.all([
        api.get(`/admin/earnings/periods/${month}`).then((r) => r.data).catch(() => null),
        api.get(`/admin/earnings/creators/${month}`).then((r) => r.data).catch(() => []),
        api.get(`/admin/payouts?month=${month}`).then((r) => r.data).catch(() => []),
      ]);
      setPeriod(p);
      setCreatorRows(cr || []);
      setPayouts(po || []);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { loadPeriods(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (selected) loadPeriodDetail(selected); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const compute = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/admin/earnings/periods/${selected}/compute`);
      toast.success(`Recomputed ${monthLabel(selected)}.`);
      await loadPeriods();
      await loadPeriodDetail(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't compute.");
    } finally { setBusy(false); }
  };

  const close = async () => {
    if (!selected || !period) return;
    if (!window.confirm(`Close ${monthLabel(selected)}? Numbers lock in — you'll no longer be able to recompute this month.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/earnings/periods/${selected}/close`);
      toast.success(`${monthLabel(selected)} closed.`);
      await loadPeriodDetail(selected);
      await loadPeriods();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't close.");
    } finally { setBusy(false); }
  };

  const payOne = async (ce) => {
    if (!window.confirm(`Send ${fmt(ce.available_ngn)} to ${ce.channel_name || ce.creator_email} via Paystack Transfer? This cannot be undone.`)) return;
    setPayingId(ce.id);
    try {
      const { data } = await api.post(`/admin/payouts/creator-earnings/${ce.id}/pay`);
      toast.success(data.status === "success" ? "Payout sent!" : `Transfer ${data.status} — Paystack will confirm via webhook.`);
      await loadPeriodDetail(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send.");
    } finally { setPayingId(null); }
  };

  const payAll = async () => {
    const eligible = creatorRows.filter((r) => r.status === "eligible");
    if (eligible.length === 0) return toast.error("No eligible creators for this month.");
    if (!window.confirm(`Send Paystack transfers to ${eligible.length} creator${eligible.length === 1 ? "" : "s"} for ${monthLabel(selected)}? Total ≈ ${fmt(eligible.reduce((s, r) => s + r.available_ngn, 0))}.`)) return;
    setBatchBusy(true);
    try {
      const { data } = await api.post(`/admin/payouts/batch/${selected}`);
      toast.success(`Batch: ${data.initiated} sent, ${data.failed} failed, ${data.skipped_no_bank} skipped (no bank).`);
      await loadPeriodDetail(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch failed.");
    } finally { setBatchBusy(false); }
  };

  const runAuto = async () => {
    if (!window.confirm("Force-run the monthly auto-payout for the previous month right now? Idempotent — creators already paid won't be paid again.")) return;
    setAutoBusy(true);
    try {
      const { data } = await api.post("/admin/payouts/auto-run");
      if (data.skipped) toast.info(`Auto-payout skipped: ${data.skipped.replace(/_/g, " ")}${data.month ? ` (${monthLabel(data.month)})` : ""}.`);
      else toast.success(`Auto-payout ${monthLabel(data.month)} — ${data.initiated} sent, ${data.skipped_no_bank} no-bank (${data.unclaimed_nudged} nudged), ${data.failed} failed.`);
      if (data.month) {
        setSelected(data.month);
        await loadPeriods();
        await loadPeriodDetail(data.month);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-payout failed.");
    } finally { setAutoBusy(false); }
  };

  const isClosed = period?.status === "closed";

  // Also include current month as a selectable option if not already computed.
  const options = useMemo(() => {
    const set = new Set(periods.map((p) => p.month));
    const now = currentMonthKey();
    if (!set.has(now)) return [{ month: now, status: "open" }, ...periods];
    return periods;
  }, [periods]);

  return (
    <div data-testid="admin-earnings-tab">
      <div className="card-surface p-5 sm:p-6 mb-6" data-testid="earnings-period-picker">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <p className="text-eyebrow">Period</p>
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              <select
                value={selected || ""}
                onChange={(e) => setSelected(e.target.value)}
                className="input-dark max-w-xs"
                data-testid="period-select"
              >
                {options.map((p) => (
                  <option key={p.month} value={p.month}>
                    {monthLabel(p.month)} {p.status === "closed" ? "· closed" : ""}
                  </option>
                ))}
              </select>
              {period && (
                <span className={`badge-pill text-[10px] ${
                  period.status === "closed" ? "bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/25" :
                  "bg-[#EAB308]/10 text-[#EAB308] border border-[#EAB308]/25"
                }`}>{period.status}</span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={compute} disabled={busy || isClosed} className="ghost-btn" data-testid="compute-period-button">
              <Calculator className="h-4 w-4" /> {busy ? "Computing…" : "Recompute"}
            </button>
            <button onClick={close} disabled={busy || !period || isClosed} className="ghost-btn hover:border-[#22C55E]/50 hover:text-[#22C55E]" data-testid="close-period-button">
              <Lock className="h-4 w-4" /> Close month
            </button>
            <button onClick={payAll} disabled={batchBusy || busy || !period || creatorRows.filter((r) => r.status === "eligible").length === 0} className="ember-btn" data-testid="pay-all-button">
              {batchBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Pay all eligible
            </button>
            <button onClick={runAuto} disabled={autoBusy} className="ghost-btn hover:border-[#EAB308]/50 hover:text-[#EAB308]" data-testid="run-auto-payout-button" title="Force-run the monthly auto-payout job for the previous month">
              {autoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              Run auto-payout
            </button>
          </div>
        </div>
      </div>

      {period?.auto_payout_done && (
        <div className="card-surface p-4 mb-6 border-[#22C55E]/25 bg-[#22C55E]/[0.03] flex items-start gap-3" data-testid="auto-payout-banner">
          <CheckCircle2 className="h-5 w-5 text-[#22C55E] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm">
              <b className="text-[#22C55E]">Auto-payout ran</b> for {monthLabel(period.month)}
              {period.auto_payout_started_at ? ` on ${new Date(period.auto_payout_started_at).toLocaleString()}` : ""}.
            </p>
            {period.auto_payout_summary && (
              <p className="text-xs text-white/50 mt-1 tabular-nums">
                {period.auto_payout_summary.initiated} transfer{period.auto_payout_summary.initiated === 1 ? "" : "s"} sent
                {" · "}{period.auto_payout_summary.skipped_no_bank} skipped (no bank
                {period.auto_payout_summary.unclaimed_nudged ? `, ${period.auto_payout_summary.unclaimed_nudged} nudged` : ""})
                {" · "}{period.auto_payout_summary.failed} failed
              </p>
            )}
          </div>
        </div>
      )}

      {!period ? (
        <div className="card-surface p-12 text-center text-white/50" data-testid="no-period">
          {busy ? <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#E65F2B]" /> : (
            <>
              <AlertCircle className="h-6 w-6 mx-auto text-white/30 mb-3" />
              <p>No data yet for this month. Click <b>Recompute</b> to generate the roster.</p>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Period KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="card-surface p-5" data-testid="kpi-matured">
              <Wallet className="h-4 w-4 text-[#E65F2B] mb-2" />
              <p className="text-eyebrow">Matured revenue</p>
              <p className="font-display text-2xl font-black mt-1">{fmt(period.matured_paid_up_ngn)}</p>
              <p className="text-xs text-white/40 mt-1">60% pool: {fmt(period.sbc_pool_ngn + period.dbc_pool_ngn + period.lbc_pool_ngn)}</p>
            </div>
            <div className="card-surface p-5" data-testid="kpi-streams-total">
              <PlayCircle className="h-4 w-4 text-[#E65F2B] mb-2" />
              <p className="text-eyebrow">Qualified streams</p>
              <p className="font-display text-2xl font-black mt-1">{period.total_streams.toLocaleString()}</p>
              <p className="text-xs text-white/40 mt-1">Unit SBC: {fmt(period.unit_sbc_ngn.toFixed(2))}</p>
            </div>
            <div className="card-surface p-5" data-testid="kpi-minutes-total">
              <Timer className="h-4 w-4 text-[#EAB308] mb-2" />
              <p className="text-eyebrow">Watch minutes</p>
              <p className="font-display text-2xl font-black mt-1">{Math.round(period.total_duration_minutes).toLocaleString()}</p>
              <p className="text-xs text-white/40 mt-1">Unit DBC: {fmt(period.unit_dbc_ngn_per_min.toFixed(2))}/min</p>
            </div>
            <div className="card-surface p-5" data-testid="kpi-listeners-total">
              <Users2 className="h-4 w-4 text-[#60A5FA] mb-2" />
              <p className="text-eyebrow">Unique listeners</p>
              <p className="font-display text-2xl font-black mt-1">{period.total_unique_listeners.toLocaleString()}</p>
              <p className="text-xs text-white/40 mt-1">Unit LBC: {fmt(period.unit_lbc_ngn.toFixed(2))}</p>
            </div>
          </div>

          {/* Pool split visualization */}
          <div className="card-surface p-5 mb-6" data-testid="pool-split">
            <p className="text-eyebrow mb-3">Commission pool split</p>
            <div className="flex h-8 rounded-lg overflow-hidden border border-white/5">
              <div className="bg-[#E65F2B] flex items-center justify-center text-xs font-bold" style={{ width: "41.66%" }} title="SBC — Stream count · 25%">SBC · {fmt(period.sbc_pool_ngn)}</div>
              <div className="bg-[#EAB308] flex items-center justify-center text-xs font-bold text-[#111]" style={{ width: "33.33%" }} title="DBC — Duration · 20%">DBC · {fmt(period.dbc_pool_ngn)}</div>
              <div className="bg-[#60A5FA] flex items-center justify-center text-xs font-bold text-[#111]" style={{ width: "25%" }} title="LBC — Listeners · 15%">LBC · {fmt(period.lbc_pool_ngn)}</div>
            </div>
            <p className="text-xs text-white/40 mt-2">25% streams + 20% duration + 15% listeners = 60% of matured revenue.</p>
          </div>

          {/* Creator earnings table */}
          <h3 className="font-display text-lg font-bold mb-3">Creators ({creatorRows.length})</h3>
          {creatorRows.length === 0 ? (
            <div className="card-surface p-10 text-center text-white/50" data-testid="no-creators">No qualified streams this month.</div>
          ) : (
            <div className="card-surface overflow-hidden" data-testid="creators-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/5 text-left">
                      <th className="px-4 py-3 text-eyebrow">Creator</th>
                      <th className="px-4 py-3 text-eyebrow text-right">Streams</th>
                      <th className="px-4 py-3 text-eyebrow text-right">Mins</th>
                      <th className="px-4 py-3 text-eyebrow text-right">Listeners</th>
                      <th className="px-4 py-3 text-eyebrow text-right">SBC</th>
                      <th className="px-4 py-3 text-eyebrow text-right">DBC</th>
                      <th className="px-4 py-3 text-eyebrow text-right">LBC</th>
                      <th className="px-4 py-3 text-eyebrow text-right">Carry</th>
                      <th className="px-4 py-3 text-eyebrow text-right">Total</th>
                      <th className="px-4 py-3 text-eyebrow">Status</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {creatorRows.map((r) => (
                      <tr key={r.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`creator-row-${r.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-display font-bold truncate max-w-[220px]">{r.channel_name || "—"}</p>
                          <p className="text-xs text-white/40 truncate max-w-[220px]">{r.creator_email}</p>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{r.streams.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{Number(r.duration_minutes).toFixed(1)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{r.unique_listeners.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-white/60">{fmt(r.sbc_earning_ngn)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-white/60">{fmt(r.dbc_earning_ngn)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-white/60">{fmt(r.lbc_earning_ngn)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-[#60A5FA]">{fmt(r.carry_from_previous_ngn)}</td>
                        <td className="px-4 py-3 text-right tabular-nums font-display font-black text-[#EAB308]">{fmt(r.available_ngn)}</td>
                        <td className="px-4 py-3">
                          <span className={`badge-pill text-[10px] ${
                            r.status === "paid" ? "bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/25" :
                            r.status === "eligible" ? "bg-[#EAB308]/10 text-[#EAB308] border border-[#EAB308]/25" :
                            r.status === "carried_forward" ? "bg-[#3B82F6]/10 text-[#60A5FA] border border-[#3B82F6]/25" :
                            "bg-white/5 text-white/40 border border-white/10"
                          }`}>{r.status.replace("_", " ")}</span>
                        </td>
                        <td className="px-4 py-3">
                          {r.status === "eligible" && (
                            <button
                              onClick={() => payOne(r)}
                              disabled={payingId === r.id}
                              className="ghost-btn !py-1.5 !px-3 text-xs hover:border-[#22C55E]/50 hover:text-[#22C55E]"
                              data-testid={`pay-creator-${r.id}`}
                            >
                              {payingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                              Pay
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payouts already made this month */}
          {payouts.length > 0 && (
            <>
              <h3 className="font-display text-lg font-bold mt-8 mb-3">Payouts this month ({payouts.length})</h3>
              <div className="space-y-2" data-testid="admin-payouts-list">
                {payouts.map((p) => (
                  <div key={p.id} className="card-surface p-4 flex items-center justify-between gap-3" data-testid={`admin-payout-${p.id}`}>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display font-bold">{fmt(p.amount_ngn)}</p>
                        <p className="text-sm text-white/60">→ {p.channel_name || p.creator_email}</p>
                        <span className={`badge-pill text-[10px] ${
                          p.status === "success" ? "bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/25" :
                          p.status === "failed" || p.status === "reversed" ? "bg-red-500/10 text-red-400 border border-red-500/25" :
                          "bg-[#EAB308]/10 text-[#EAB308] border border-[#EAB308]/25"
                        }`}>{p.status}</span>
                      </div>
                      <p className="text-xs text-white/40 mt-1">
                        {p.bank_name} ****{(p.account_number || "").slice(-4)} ·
                        {" "}{new Date(p.created_at).toLocaleString()}
                        {p.reference ? ` · ${p.reference}` : ""}
                      </p>
                      {p.failure_reason && <p className="text-xs text-red-400 mt-0.5">{p.failure_reason}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
